import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import chatInternoService, { ChatConversa, ChatMensagem } from '../services/chatInternoService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ── Avatar com fallback para iniciais ────────────────────────────────────────
const getAvatarSrc = (avatar?: string | null) => {
  if (!avatar) return null;
  if (avatar.startsWith('data:') || avatar.startsWith('http')) return avatar;
  return null;
};
const getInitials = (nome: string) => {
  const p = nome.trim().split(' ').filter(Boolean);
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : (p[0]?.slice(0, 2) || '??').toUpperCase();
};
const COLORS = ['#c0392b', '#2980b9', '#27ae60', '#8e44ad', '#e67e22', '#16a085', '#d35400', '#1a1a2e'];
const avatarColor = (id: string) => { let h = 0; for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h); return COLORS[Math.abs(h) % COLORS.length]; };

interface AvatarProps { nome: string; avatar?: string | null; id?: string; size?: number; className?: string; }
const AvatarCircle: React.FC<AvatarProps> = ({ nome, avatar, id, size = 32, className = '' }) => {
  const src = getAvatarSrc(avatar);
  const [err, setErr] = useState(false);
  const color = avatarColor(id || nome);
  const style: React.CSSProperties = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };
  if (src && !err) return <img src={src} alt={nome} onError={() => setErr(true)} style={{ ...style, objectFit: 'cover' }} className={className} />;
  return <div style={{ ...style, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: size * 0.38, fontWeight: 700 }} className={className}>{getInitials(nome)}</div>;
};

interface UsuarioChat {
  id: string;
  nome: string;
  email: string;
  cargo: string;
  avatar?: string;
  departamento_nome: string;
  total_conversas: number;
  total_mensagens: number;
  ultima_atividade?: string;
}

interface ResultadoBuscaMensagem {
  id: string;
  conversa_id: string;
  conteudo: string;
  data_criacao: string;
  remetente_id: string;
  remetente_nome: string;
  remetente_avatar?: string;
  conversa_tipo: 'direto' | 'grupo';
  conversa_nome?: string;
  membros?: { id: string; nome: string }[];
}

const MonitorChatPage: React.FC = () => {
  const [aba, setAba] = useState<'ativas' | 'excluidas'>('ativas');
  const [usuarios, setUsuarios] = useState<UsuarioChat[]>([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<UsuarioChat | null>(null);
  const [conversas, setConversas] = useState<ChatConversa[]>([]);
  const [conversaSelecionada, setConversaSelecionada] = useState<ChatConversa | null>(null);
  const [mensagens, setMensagens] = useState<ChatMensagem[]>([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [buscaMensagens, setBuscaMensagens] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState<ResultadoBuscaMensagem[]>([]);
  const [buscandoMensagens, setBuscandoMensagens] = useState(false);
  const [mensagemDestacadaId, setMensagemDestacadaId] = useState<string | null>(null);
  const [conversasExcluidas, setConversasExcluidas] = useState<any[]>([]);
  const [carregandoExcluidas, setCarregandoExcluidas] = useState(false);
  const [conversaExcluidaSelecionada, setConversaExcluidaSelecionada] = useState<any | null>(null);
  const [mensagensExcluida, setMensagensExcluida] = useState<ChatMensagem[]>([]);
  const [carregandoMsgsExcluida, setCarregandoMsgsExcluida] = useState(false);
  const mensagensEndRef = useRef<HTMLDivElement>(null);
  const mensagensContainerRef = useRef<HTMLDivElement>(null);
  const mensagensExcluidaContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get('/chat-interno/admin/usuarios')
      .then(res => setUsuarios(res.data.usuarios || []))
      .catch(() => {})
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    if (!usuarioSelecionado) return;
    setConversas([]);
    setConversaSelecionada(null);
    setMensagens([]);
    api.get(`/chat-interno/admin/usuarios/${usuarioSelecionado.id}/conversas`)
      .then(res => setConversas(res.data.conversas || []))
      .catch(() => {});
  }, [usuarioSelecionado]);

  useEffect(() => {
    if (!conversaSelecionada) return;
    chatInternoService.buscarMensagens(conversaSelecionada.id)
      .then(setMensagens)
      .catch(() => {});
  }, [conversaSelecionada]);

  // Rola apenas o container interno de mensagens, nunca a página
  useEffect(() => {
    if (mensagens.length > 0 && mensagensContainerRef.current) {
      mensagensContainerRef.current.scrollTop = mensagensContainerRef.current.scrollHeight;
    }
  }, [mensagens]);

  // Garante que a página sempre inicia no topo ao navegar para cá
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Busca de mensagens por conteúdo (com debounce, entre colaboradores/conversas)
  useEffect(() => {
    const termo = buscaMensagens.trim();
    if (termo.length < 2) {
      setResultadosBusca([]);
      setBuscandoMensagens(false);
      return;
    }
    setBuscandoMensagens(true);
    const timer = setTimeout(() => {
      api.get(`/chat-interno/admin/mensagens/buscar?q=${encodeURIComponent(termo)}`)
        .then(res => setResultadosBusca(res.data.mensagens || []))
        .catch(() => setResultadosBusca([]))
        .finally(() => setBuscandoMensagens(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [buscaMensagens]);

  // Ao clicar num resultado de busca: localiza o colaborador (remetente),
  // abre a conversa correspondente e rola até a mensagem encontrada.
  const abrirResultadoBusca = async (resultado: ResultadoBuscaMensagem) => {
    const colaborador = usuarios.find(u => u.id === resultado.remetente_id)
      || usuarios.find(u => resultado.membros?.some(m => m.id === u.id));
    if (colaborador) {
      setUsuarioSelecionado(colaborador);
    }
    try {
      const res = await api.get(`/chat-interno/conversas/${resultado.conversa_id}/mensagens`);
      const msgs: ChatMensagem[] = res.data.mensagens || [];
      setMensagens(msgs);
      setConversaSelecionada({
        id: resultado.conversa_id,
        tipo: resultado.conversa_tipo,
        nome: resultado.conversa_nome,
        data_criacao: resultado.data_criacao,
        total_mensagens: msgs.length,
        nao_lidas: 0,
        membros: (resultado.membros || []).map(m => ({ id: m.id, nome: m.nome })),
      });
    } catch {}
    setMensagemDestacadaId(resultado.id);
    setTimeout(() => {
      document.getElementById(`msg-${resultado.id}`)?.scrollIntoView({ block: 'center' });
    }, 100);
    setTimeout(() => setMensagemDestacadaId(null), 3000);
  };

  const formatarData = (data?: string) => {
    if (!data) return '—';
    const d = new Date(data);
    const hoje = new Date();
    if (d.toDateString() === hoje.toDateString())
      return format(d, 'HH:mm', { locale: ptBR });
    return format(d, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  const nomeConversa = (c: ChatConversa) => {
    if (c.tipo === 'grupo') return c.nome || 'Grupo';
    const outro = c.membros?.find(m => m.id !== usuarioSelecionado?.id);
    return outro?.nome || 'Conversa direta';
  };

  const usuariosFiltrados = usuarios.filter(u =>
    u.nome.toLowerCase().includes(busca.toLowerCase()) ||
    u.email.toLowerCase().includes(busca.toLowerCase()) ||
    u.departamento_nome.toLowerCase().includes(busca.toLowerCase())
  );

  useEffect(() => {
    if (aba !== 'excluidas') return;
    setCarregandoExcluidas(true);
    setConversaExcluidaSelecionada(null);
    setMensagensExcluida([]);
    api.get('/chat-interno/admin/conversas-excluidas')
      .then(res => setConversasExcluidas(res.data.conversas || []))
      .catch(() => {})
      .finally(() => setCarregandoExcluidas(false));
  }, [aba]);

  useEffect(() => {
    if (!conversaExcluidaSelecionada) return;
    setCarregandoMsgsExcluida(true);
    chatInternoService.buscarMensagens(conversaExcluidaSelecionada.id)
      .then(msgs => {
        setMensagensExcluida(msgs);
        setTimeout(() => {
          if (mensagensExcluidaContainerRef.current) {
            mensagensExcluidaContainerRef.current.scrollTop = mensagensExcluidaContainerRef.current.scrollHeight;
          }
        }, 50);
      })
      .catch(() => {})
      .finally(() => setCarregandoMsgsExcluida(false));
  }, [conversaExcluidaSelecionada]);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Monitor de Chat</h2>

      {/* Abas */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setAba('ativas')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${aba === 'ativas' ? 'bg-bravo-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Conversas Ativas
        </button>
        <button onClick={() => setAba('excluidas')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${aba === 'excluidas' ? 'bg-bravo-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Excluídas (90 dias)
        </button>
      </div>

      {/* Busca por conteúdo de mensagem (aba Ativas) */}
      {aba === 'ativas' && (
        <div className="relative mb-4" style={{ maxWidth: 480 }}>
          <div className="relative">
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              value={buscaMensagens}
              onChange={e => setBuscaMensagens(e.target.value)}
              placeholder="Buscar palavra ou trecho em mensagens..."
              className="w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-bravo-primary"
            />
          </div>

          {buscaMensagens.trim().length >= 2 && (
            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto">
              {buscandoMensagens ? (
                <div className="text-center text-gray-400 text-sm p-4">Buscando...</div>
              ) : resultadosBusca.length === 0 ? (
                <div className="text-center text-gray-400 text-sm p-4 italic">Nenhuma mensagem encontrada.</div>
              ) : (
                <>
                  <p className="text-xs text-gray-400 px-3 pt-2 pb-1">{resultadosBusca.length} resultado(s)</p>
                  {resultadosBusca.map(r => (
                    <div
                      key={r.id}
                      onClick={() => abrirResultadoBusca(r)}
                      className="px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-t border-gray-50 dark:border-gray-700"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {r.remetente_nome}
                          {r.conversa_tipo === 'grupo' && (
                            <span className="text-gray-400 dark:text-gray-500 font-normal"> · {r.conversa_nome || 'Grupo'}</span>
                          )}
                        </p>
                        <span className="text-xs text-gray-400 flex-shrink-0">{formatarData(r.data_criacao)}</span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{r.conteudo}</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Aba Excluídas */}
      {aba === 'excluidas' && (
        <div className="flex gap-4" style={{ height: 'calc(100vh - 230px)' }}>

          {/* Coluna 1 — Lista de conversas excluídas */}
          <div className="bg-white rounded-lg shadow-md flex flex-col" style={{ width: 320, flexShrink: 0 }}>
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Excluídas nos últimos 90 dias
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {carregandoExcluidas ? (
                <div className="text-center text-gray-400 text-sm p-6">Carregando...</div>
              ) : conversasExcluidas.length === 0 ? (
                <div className="text-center text-gray-400 text-sm p-6 italic">Nenhuma conversa excluída nos últimos 90 dias.</div>
              ) : conversasExcluidas.map(c => (
                <div
                  key={c.id}
                  onClick={() => setConversaExcluidaSelecionada(c)}
                  className={`p-3 cursor-pointer border-b border-gray-50 hover:bg-gray-50 transition-colors ${conversaExcluidaSelecionada?.id === c.id ? 'bg-red-50 border-l-4 border-red-400' : 'border-l-4 border-transparent'}`}
                >
                  <div className="flex items-start justify-between gap-1">
                    <p className="text-sm font-semibold text-gray-800 truncate flex items-center gap-1">
                      {c.tipo === 'grupo' ? (
                        <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 100-8 4 4 0 000 8zm6 3c0 2-2 3-6 3s-6-1-6-3 2-3 6-3 6 1 6 3z" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                      {c.tipo === 'grupo' ? (c.nome || 'Grupo') : (c.membros?.map((m: any) => m.nome).join(' & ') || 'Conversa')}
                    </p>
                    <span className="bg-red-50 text-red-500 px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0">Excluída</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    Criada por: {c.criador_nome} • {c.total_mensagens} mensagens
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    {c.membros?.map((m: any) => m.nome).join(', ')}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{formatarData(c.data_criacao)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Coluna 2 — Mensagens da conversa excluída */}
          <div className="bg-white rounded-lg shadow-md flex flex-col flex-1 min-w-0">
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                {conversaExcluidaSelecionada
                  ? (conversaExcluidaSelecionada.tipo === 'grupo'
                    ? (conversaExcluidaSelecionada.nome || 'Grupo')
                    : conversaExcluidaSelecionada.membros?.map((m: any) => m.nome).join(' & '))
                  : 'Selecione uma conversa'}
              </p>
              {conversaExcluidaSelecionada && (
                <p className="text-xs text-gray-400 mt-1">
                  Membros: {conversaExcluidaSelecionada.membros?.map((m: any) => m.nome).join(', ')}
                </p>
              )}
            </div>

            <div ref={mensagensExcluidaContainerRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
              {!conversaExcluidaSelecionada ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                  <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                  </svg>
                  <p className="text-sm">Selecione uma conversa para ver as mensagens</p>
                </div>
              ) : carregandoMsgsExcluida ? (
                <div className="text-center text-gray-400 text-sm mt-8">Carregando mensagens...</div>
              ) : mensagensExcluida.length === 0 ? (
                <div className="text-center text-gray-400 text-sm mt-8">Nenhuma mensagem nesta conversa</div>
              ) : mensagensExcluida.map(m => (
                <div key={m.id} className="flex gap-3">
                  <AvatarCircle nome={m.remetente_nome} avatar={m.remetente_avatar} id={m.remetente_id} size={32} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold text-gray-800">{m.remetente_nome}</span>
                      <span className="text-xs text-gray-400">{formatarData(m.data_criacao)}</span>
                    </div>
                    {m.conteudo && <p className="text-sm text-gray-700 mt-0.5">{m.conteudo}</p>}
                    {m.anexos?.map(a => (
                      <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                        className="text-xs text-bravo-primary underline mt-1 flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {a.nome_original}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* Aba Ativas */}
      {aba === 'ativas' && <div className="flex gap-4" style={{ height: 'calc(100vh - 230px)' }}>

        {/* Coluna 1 — Usuários */}
        <div className="bg-white rounded-lg shadow-md flex flex-col" style={{ width: 260, flexShrink: 0 }}>
          <div className="p-4 border-b border-gray-100">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-2">Colaboradores</p>
            <input
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar usuário..."
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-bravo-primary"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {carregando ? (
              <div className="text-center text-gray-400 text-sm p-6">Carregando...</div>
            ) : usuariosFiltrados.map(u => (
              <div
                key={u.id}
                onClick={() => setUsuarioSelecionado(u)}
                className={`p-3 cursor-pointer border-b border-gray-50 hover:bg-gray-50 transition-colors ${usuarioSelecionado?.id === u.id ? 'bg-blue-50 border-l-4 border-bravo-primary' : 'border-l-4 border-transparent'}`}
              >
                <div className="flex items-center gap-2">
                  <AvatarCircle nome={u.nome} avatar={u.avatar} id={u.id} size={32} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{u.nome}</p>
                    <p className="text-xs text-gray-500 truncate">{u.departamento_nome}</p>
                  </div>
                </div>
                <div className="flex gap-3 mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {u.total_conversas} conv.
                  </span>
                  <span className="flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {u.total_mensagens} msg
                  </span>
                </div>
                {u.ultima_atividade && (
                  <p className="text-xs text-gray-400 mt-1">Último: {formatarData(u.ultima_atividade)}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Coluna 2 — Conversas do usuário */}
        <div className="bg-white rounded-lg shadow-md flex flex-col" style={{ width: 260, flexShrink: 0 }}>
          <div className="p-4 border-b border-gray-100">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
              {usuarioSelecionado ? `Conversas de ${usuarioSelecionado.nome}` : 'Selecione um usuário'}
            </p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {!usuarioSelecionado ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                </svg>
                <p className="text-sm">Selecione um colaborador</p>
              </div>
            ) : conversas.length === 0 ? (
              <div className="text-center text-gray-400 text-sm p-6">Nenhuma conversa encontrada</div>
            ) : conversas.map(c => (
              <div
                key={c.id}
                onClick={() => setConversaSelecionada(c)}
                className={`p-3 cursor-pointer border-b border-gray-50 hover:bg-gray-50 transition-colors ${conversaSelecionada?.id === c.id ? 'bg-blue-50 border-l-4 border-bravo-primary' : 'border-l-4 border-transparent'}`}
              >
                <div className="flex items-start justify-between gap-1">
                  <p className="text-sm font-medium text-gray-800 truncate flex items-center gap-1">
                    {c.tipo === 'grupo' ? (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 100-8 4 4 0 000 8zm6 3c0 2-2 3-6 3s-6-1-6-3 2-3 6-3 6 1 6 3z" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                    {nomeConversa(c)}
                  </p>
                  <span className="text-xs text-gray-400 flex-shrink-0">{formatarData(c.data_ultima_mensagem)}</span>
                </div>
                {c.ultima_mensagem && (
                  <p className="text-xs text-gray-500 mt-1 truncate">{c.ultima_mensagem}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">{c.total_mensagens} mensagens • {c.membros?.length} membros</p>
              </div>
            ))}
          </div>
        </div>

        {/* Coluna 3 — Mensagens */}
        <div className="bg-white rounded-lg shadow-md flex flex-col flex-1 min-w-0">
          <div className="p-4 border-b border-gray-100">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
              {conversaSelecionada ? nomeConversa(conversaSelecionada) : 'Selecione uma conversa'}
            </p>
            {conversaSelecionada && (
              <p className="text-xs text-gray-400 mt-1">
                Membros: {conversaSelecionada.membros?.map(m => m.nome).join(', ')}
              </p>
            )}
          </div>

          <div ref={mensagensContainerRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
            {!conversaSelecionada ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-sm">Selecione uma conversa para ver as mensagens</p>
              </div>
            ) : mensagens.length === 0 ? (
              <div className="text-center text-gray-400 text-sm mt-8">Nenhuma mensagem nesta conversa</div>
            ) : mensagens.map(m => (
              <div
                key={m.id}
                id={`msg-${m.id}`}
                className={`flex gap-3 rounded-md transition-colors ${mensagemDestacadaId === m.id ? 'bg-yellow-100 dark:bg-yellow-900/40 -mx-2 px-2 py-1' : ''}`}
              >
                <AvatarCircle nome={m.remetente_nome} avatar={m.remetente_avatar} id={m.remetente_id} size={32} className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-gray-800">{m.remetente_nome}</span>
                    <span className="text-xs text-gray-400">{formatarData(m.data_criacao)}</span>
                  </div>
                  {m.conteudo && <p className="text-sm text-gray-700 mt-0.5">{m.conteudo}</p>}
                  {m.anexos?.map(a => (
                    <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                      className="text-xs text-bravo-primary underline mt-1 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      {a.nome_original}
                    </a>
                  ))}
                </div>
              </div>
            ))}
            <div ref={mensagensEndRef} />
          </div>
        </div>

      </div>}

    </div>
  );
};

export default MonitorChatPage;
